<template>
  <RevContainer>
    <div class="grid-standard mb-72 grid py-24 lg:pt-56">
      <div class="col-span-full lg:col-span-8 lg:col-start-2">
        <h2 class="heading-3 mb-32">{{ i18n(translations.nextStepsTitle) }}</h2>

        <div class="space-y-0">
          <div
            v-for="(step, index) in steps"
            :key="step.id"
            class="flex items-start gap-x-16"
          >
            <div class="flex flex-col items-center">
              <div
                class="bg-static-info-mid rounded-full flex h-40 w-40 items-center justify-center"
              >
                <component :is="step.icon" class="h-24 w-24" />
              </div>
              <!-- Only show divider if not the last step -->
              <RevDivider
                v-if="index < steps.length - 1"
                class="my-4 h-60 w-1 lg:h-32"
                variant="vertical"
              />
            </div>
            <div class="flex-1" :class="{ 'pb-24': index < steps.length - 1 }">
              <h3 class="text-static-default-hi body-1-bold mb-4">
                {{ step.title }}
              </h3>
              <p class="text-static-default-low body-2">
                {{ step.description }}
              </p>
            </div>
          </div>
        </div>
        <RevLink
          class="body-1-link mt-24 block"
          target="_blank"
          :to="expressReplacementFaqArticleLink"
        >
          {{ i18n(translations.footerCta) }}
        </RevLink>
      </div>
    </div>
  </RevContainer>
</template>

<script lang="ts" setup>
import { useRuntimeConfig, useZendeskFaqBaseUrl } from '#imports'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { IconCamera } from '@ds/icons/IconCamera'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconExpressReplacement } from '@ds/icons/IconExpressReplacement'
import { IconPackage } from '@ds/icons/IconPackage'

import { isConfigDefined } from '~/scopes/care-commons/utils/isConfigDefined'

import translations from './ExpressReplacementConfirmation.translations'

interface ExpressReplacementConfirmationNextStepsProps {
  returnWindow: Date
  deliveryDates: {
    min: Date
    max: Date
  }
}

const { returnWindow, deliveryDates } =
  defineProps<ExpressReplacementConfirmationNextStepsProps>()

const i18n = useI18n()

const { ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE } = useRuntimeConfig().public
const { zendeskFaqBaseUrl } = useZendeskFaqBaseUrl()

const expressReplacementFaqArticleLink = toNuxtLink({
  type: 'external',
  href: `${zendeskFaqBaseUrl}/articles/${isConfigDefined(ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE, 'ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE')}`,
})

const steps = [
  {
    id: 'replacement',
    icon: IconExpressReplacement,
    title: i18n(translations.replacementStepTitle),
    description: i18n(translations.replacementStepDescription, {
      min: deliveryDates.min,
      max: deliveryDates.max,
    }),
  },
  {
    id: 'transfer',
    icon: IconDownload,
    title: i18n(translations.transferStepTitle),
    description: i18n(translations.transferStepDescription),
  },
  {
    id: 'photos',
    icon: IconCamera,
    title: i18n(translations.photosStepTitle),
    description: i18n(translations.photosStepDescription),
  },
  {
    id: 'return',
    icon: IconPackage,
    title: i18n(translations.returnStepTitle),
    description: i18n(translations.returnStepDescription, {
      deadline: returnWindow,
    }),
  },
]
</script>
